<template>
	<div class="stretch-layout">
		<card xs-tile sm-tile class="flex-grow-1" :no-spacing="$breakpoint.xsOnly">
			<card-text class="p-0" fill>
				<client-only>
					<event-map ref="map" :event="eventStore.event" :participation-classes="participationClasses"
							   v-model:selected-participation-class-keys="selectedParticipationClassKeys"
							   :additional-zoom-elements="participationLiveLocations"
							   :zoom="15"
							   v-model:bounds="mapBounds"
							   :track-spots="trackSpots" :tracks="tracks" with-types-filter>
						<template v-for="participation in participations">
							<participation-marker v-if="isParticipationVisible(participation)"
												  :key="`marker-${participation.id}`"
												  :participation="participation"
												  :color="participationClassColors[participation.participation_class_id] ?? '#47a447'"
												  :focused="!focusedParticipationIds.length ? null : focusedParticipationIds.includes(participation.id)"
												  :lat-lng="participation.liveLocation" @click="showParticipationDetails(participation)"/>
						</template>

						<template #footer>
							<wrapper stretch class="p-8 mt-0" break-column="xs">
								<form-select name="participations"
											 :placeholder="$t('placeholder.select_participations')"
											 :options="participationOptions"
											 v-model="focusedParticipationIds"
											 :color="eventStore.colorStyle"
											 inverted
											 hide-details
											 multiple
											 filterable
											 clearable/>
								<template v-if="!isActive">
									<alert v-if="$date(eventStore.event.end_date).isBefore()" :content="$t('text.event_finished')" type="success" :icon="icons.check" small no-spacing/>
									<alert v-else :content="$t('text.event_not_started')" small no-spacing/>
								</template>
							</wrapper>
						</template>
					</event-map>

					<slide-sheet v-if="selectedParticipation" v-model="isParticipationDetailsModalVisible" :width="320">
						<template #default>
							<participation-popup-card :participation="selectedParticipation"
													  :lat-lng="selectedParticipation.liveLocation"
													  :pace-type="participationClasses.length ? participationClasses[0].settings.pace_type : null"
													  :live="isActive"
													  :to="$web('events.participations.show', [eventStore.event._key, selectedParticipation.id])"
							/>
						</template>
					</slide-sheet>
				</client-only>
			</card-text>
		</card>
	</div>
</template>

<script setup lang="ts">
import {useEventStore} from "../../../store/event";
import {EventPageType, EventStatus, TrackSpotType} from "@spoferan/spoferan-ts-core";
import {checkIcon} from "@spoferan/nuxt-spoferan/icons";

const eventStore = useEventStore();
const {t} = useI18n();
const {$api, $web, $apiFetch, $echo, $date} = useNuxtApp();
const route = useRoute();
useScreenSaverPrevention();

// Make sure the page is activated
if (!eventStore.hasActivePage(EventPageType.Live)) {
	await navigateTo($web('events.show', route.params.event));
}

const dataRequests = [
	useApiFetch(`/v1/view/events/${eventStore.event.id}/tracking`, {
		guest: true,
		params: {
			ts: eventStore.event.map_cache_buster,
		}
	}),
	useApiFetch($api('events.participations.live.index', [eventStore.event.id]), {
		guest: true
	}),
	useApiFetch($api('events.participation_classes.index', eventStore.event.id), {
		guest: true,
		params: {
			relations: ['settings'],
			published: [EventStatus.Draft, EventStatus.Unverified].indexOf(eventStore.event.status.value) < 0
		}
	})
];

const dataResponses = await Promise.all(dataRequests);
const tracks = dataResponses[0].data.tracks;
const trackSpots = dataResponses[0].data.trackSpots;
let participationClassColors = {};
if (tracks.length) {
	tracks.forEach(track => {
		track.participationClasses.forEach(participationClass => {
			participationClassColors[participationClass.id] = track.color;
		})
	})
}

const participations = dataResponses[1].data;
// Initially focus the map to the bounding of all live participations
const participationLiveLocations = participations?.length ? participations.map(participation => participation.liveLocation).filter(location => !!location) : []

const participationClasses = dataResponses[2].data;

const mapBounds = ref(null);
const selectedParticipationClassKeys = ref([]);
const focusedParticipationIds = ref([]);

const selectedParticipation = ref(null);
const isParticipationDetailsModalVisible = ref(false);

const icons = {
	check: checkIcon,
};

const isActive = computed(() => {
	// Live tracking participations can be entered one hour before start
	return $date(eventStore.event.start_date).subtract(1, 'hour').isBefore() && $date(eventStore.event.end_date).isAfter();
});

const participationOptions = computed(() => {
	return participations && participations.length ? participations.map(participation => {
		const participationClass = participationClasses.find(participationClass => participationClass.id === participation.participation_class_id);

		return {
			value: participation.id,
			text: participation.display_name,
			image: participation.avatar_thumbnail,
			subtitle: participation.starter_number ? `#${participation.starter_number}` : null,
			group: participationClass ? participationClass.name : null
		}
	}) : [];
});

watch(isActive, (newValue, oldValue) => {
	if (newValue !== oldValue) {
		if (newValue) {
			activateLiveUpdates();
		} else {
			disableLiveUpdates();
		}
	}
});

function isLatLngInBounds(latLng) {
	return mapBounds.value && mapBounds.value.contains(latLng);
}

function activateLiveUpdates() {
	if (!isActive.value) {
		return;
	}

	console.log('Listening for location changes...');
	$echo.subscribe('event.' + eventStore.event.id)
		.listen('participation_live_location.refreshed', (location) => {
			const participation = participations.find(participation => participation.id === location.participation_id);
			if (participation) {
				participation['liveLocation'] = location;
			}
		}).listen('live_ranks.updated', (data) => {
		const participationId = data.participation_id;
		const participation = participations.find(participation => participation.id === participationId);
		if (participation) {
			participation['ranks'] = data.ranks;
		}
	}).listen('participation.updated', (updateParticipation) => {
		const participation = participations.find(participation => participation.id === updateParticipation.id);
		if (participation) {
			participation['status'] = updateParticipation.status;
			participation['started_at'] = updateParticipation.started_at;
			participation['finished_at'] = updateParticipation.finished_at;
			participation['time'] = updateParticipation.time;
			participation['pace'] = updateParticipation.pace;
			if (!updateParticipation.started_at || !!updateParticipation.finished_at) {
				participation['liveLocation'] = null;
			}
		}
	});

}

function disableLiveUpdates() {
	console.log('Stop listening for location changes...');
	$echo.unsubscribe('event.' + eventStore.event.id);
}

function isParticipationVisible(participation) {
	if (selectedParticipationClassKeys.value && selectedParticipationClassKeys.value.length && selectedParticipationClassKeys.value.length < participationClasses.length) {
		const participationClass = participationClasses.find(participationClass => participationClass.id === participation.participation_class_id);
		if (participationClass && !selectedParticipationClassKeys.value.includes(participationClass._key)) {
			return false;
		}
	}

	return isActive.value && !!participation.liveLocation;
}

function showParticipationDetails(participation) {
	selectedParticipation.value = participation;
	nextTick(() => {
		isParticipationDetailsModalVisible.value = true;
	})
}

onMounted(() => {
	if (isActive.value) {
		activateLiveUpdates();
	} else {
		disableLiveUpdates();
	}
})

useMeta({
	title: eventStore.buildPageTitle(t('view.events.tracking.index.title', {event: eventStore.event.display_name})),
	description: t('view.events.tracking.index.description', {event: eventStore.event.display_name})
})

</script>

